import React, { useState, useCallback, useEffect } from 'react';
import { getOverview, getChannels, getRetention, getDevices, getRevenue, getEvents, getAiInsights, parseReport } from '../api';

// ── Shared section title ──────────────────────────────────────────────────
function SectionTitle({ icon, text, sub }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:10, paddingBottom:12, borderBottom:'1px solid var(--border)' }}>
      <span style={{ fontSize:18 }}>{icon}</span>
      <div>
        <div style={{ fontSize:'clamp(12px, 2.5vw, 14px)', fontWeight:700, color:'var(--text)' }}>{text}</div>
        {sub && <div style={{ fontSize:'clamp(9px, 1.8vw, 11px)', color:'var(--text3)', marginTop:2 }}>{sub}</div>}
      </div>
    </div>
  );
}

// ── Priority badge ────────────────────────────────────────────────────────
function PriorityBadge({ p }) {
  const c = p==='High'?'var(--danger)':p==='Medium'?'var(--warning)':'var(--success)';
  return <span style={{ padding:'2px 9px', borderRadius:20, fontSize:'clamp(7px, 1.4vw, 9px)', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', border:`1px solid ${c}55`, background:c+'18', color:c }}>{p}</span>;
}

// ── Score circle SVG ──────────────────────────────────────────────────────
function ScoreCircle({ score }) {
  const c = score>=75?'var(--success)':score>=50?'var(--warning)':'var(--danger)';
  const pct = Math.min(score,100);
  return (
    <svg width="clamp(100px, 20vw, 130px)" height="clamp(100px, 20vw, 130px)" viewBox="0 0 130 130">
      <circle cx={65} cy={65} r={54} fill="none" stroke="var(--border2)" strokeWidth={10} />
      <circle cx={65} cy={65} r={54} fill="none" stroke={c} strokeWidth={10}
        strokeDasharray={`${(pct/100)*339} 339`} strokeLinecap="round"
        transform="rotate(-90 65 65)" style={{ transition:'stroke-dasharray 1s ease' }} />
      <text x={65} y={61} textAnchor="middle" dominantBaseline="middle" fill={c} fontSize={28} fontWeight={800} fontFamily="Space Mono">{score}</text>
      <text x={65} y={80} textAnchor="middle" fill="var(--text3)" fontSize={11} fontFamily="Sora">/100</text>
    </svg>
  );
}

// ── History management ────────────────────────────────────────────────────
const useAiInsightsHistory = () => {
  const STORAGE_KEY = 'ai_insights_history';
  const EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

  const saveToHistory = useCallback((insights, propertyName, dateRange) => {
    try {
      const history = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      const entry = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        expiresAt: Date.now() + EXPIRY_MS,
        propertyName,
        dateRange,
        insights,
      };
      const updated = [entry, ...history].slice(0, 20); // Keep 20 max
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return entry.id;
    } catch (e) {
      console.error('History save error:', e);
      return null;
    }
  }, []);

  const getHistory = useCallback(() => {
    try {
      const history = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      const now = Date.now();
      const valid = history.filter(h => h.expiresAt > now);
      if (valid.length !== history.length) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(valid));
      }
      return valid.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    } catch (e) {
      console.error('History read error:', e);
      return [];
    }
  }, []);

  const deleteHistoryItem = useCallback((id) => {
    try {
      const history = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      const updated = history.filter(h => h.id !== id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return true;
    } catch (e) {
      console.error('History delete error:', e);
      return false;
    }
  }, []);

  return { saveToHistory, getHistory, deleteHistoryItem };
};

export default function AiInsights({ propertyId, propertyName, startDate, endDate }) {
  const [insights, setInsights] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [err,      setErr]      = useState(null);
  const [progress, setProgress] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const { saveToHistory, getHistory, deleteHistoryItem } = useAiInsightsHistory();

  // Load history on mount
  useEffect(() => {
    setHistory(getHistory());
  }, [getHistory]);

  const generate = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    setErr(null);
    setInsights(null);
    setShowHistory(false);

    try {
      setProgress('Fetching user & session data…');
      const [ov, ch, ret, dev] = await Promise.all([
        getOverview(propertyId, startDate, endDate),
        getChannels(propertyId, startDate, endDate),
        getRetention(propertyId, startDate, endDate),
        getDevices(propertyId, startDate, endDate),
      ]);

      setProgress('Fetching events & revenue data…');
      const [evRes, revRes] = await Promise.all([
        getEvents(propertyId, startDate, endDate).catch(()=>null),
        getRevenue(propertyId, startDate, endDate).catch(()=>null),
      ]);

      setProgress('Sending to LLaMA 3.3 70B AI…');

      const sumRows = (data) => {
        if (!data) return { rows:[], totals:{} };
        const rows = parseReport(data);
        const totals = rows.reduce((acc,r)=>{ Object.keys(r).forEach(k=>{ if(typeof r[k]==='number') acc[k]=(acc[k]||0)+r[k]; }); return acc; },{});
        return { rows:rows.slice(0,5), totals };
      };

      // Events summary
      let evSummary = null;
      if (evRes) {
        const evRows = parseReport(evRes.data).sort((a,b)=>b.eventCount-a.eventCount);
        evSummary = {
          total: evRows.reduce((s,e)=>s+(e.eventCount||0),0),
          topEvents: evRows.slice(0,8).map(e=>({ name:e.eventName, count:e.eventCount, perUser:e.eventCountPerUser })),
          hasScreenView: evRows.some(e=>e.eventName==='screen_view'),
          hasPurchase:   evRows.some(e=>e.eventName==='purchase'),
          hasAdImpression:evRows.some(e=>e.eventName==='ad_impression'),
        };
      }

      // Revenue summary
      let revSummary = null;
      if (revRes) {
        const dailyRev  = parseReport(revRes.data.daily);
        const totPurch  = dailyRev.reduce((s,r)=>s+(+r.purchaseRevenue||0),0);
        const totAd     = dailyRev.reduce((s,r)=>s+(+r.totalAdRevenue||0),0);
        const totTx     = dailyRev.reduce((s,r)=>s+(+r.transactions||0),0);
        const ctyRows   = parseReport(revRes.data.country).map(r=>({country:r.country,rev:(+r.purchaseRevenue||0)+(+r.totalAdRevenue||0)})).sort((a,b)=>b.rev-a.rev);
        const chRows    = parseReport(revRes.data.channel).map(r=>({channel:r.sessionDefaultChannelGroup,rev:(+r.purchaseRevenue||0)+(+r.totalAdRevenue||0)})).sort((a,b)=>b.rev-a.rev);
        revSummary = {
          totalRevenue: totPurch+totAd, purchaseRevenue:totPurch, adRevenue:totAd, transactions:totTx,
          adSharePct:   totPurch+totAd>0?((totAd/(totPurch+totAd))*100).toFixed(1):0,
          iapSharePct:  totPurch+totAd>0?((totPurch/(totPurch+totAd))*100).toFixed(1):0,
          avgRevenuePerTx: totTx>0?(totPurch/totTx).toFixed(2):0,
          topCountries: ctyRows.slice(0,5),
          topChannels:  chRows.slice(0,5),
        };
      }

      const analyticsData = {
        dateRange:   { startDate, endDate },
        overview:    sumRows(ov.data),
        channels:    sumRows(ch.data),
        retention:   sumRows(ret.data),
        devices:     sumRows(dev.data),
        events:      evSummary,
        revenue:     revSummary,
      };

      const result = await getAiInsights(analyticsData, propertyName||`Property ${propertyId}`, { startDate, endDate });
      // If server returned an error object, surface it
      if (result.data?.error) throw new Error(result.data.error);
      const raw = result.data;
      // Normalize server field names to UI field names
      const normalized = {
        ...raw,
        devActions:        raw.developerActions || raw.devActions        || [],
        retentionInsights: raw.retentionPlan    || raw.retentionInsights || [],
        tips:              raw.adRevenueTips    || raw.tips              || [],
        adRevenueTips:     raw.adRevenueTips    || [],
        purchaseTips:      raw.purchaseTips     || [],
        topTopics:         raw.tags             || raw.topTopics         || [],
        revenueSummary:    raw.revenueInsight   || raw.revenueSummary    || null,
        improvements:      raw.improvements     || [],
        keyFindings:       raw.keyFindings      || [],
        quickWins:         raw.quickWins        || [],
      };
      setInsights(normalized);
      saveToHistory(normalized, propertyName, { startDate, endDate });
      setHistory(getHistory());
      setProgress('');
    } catch(e) {
      // Surface API error message from response body if available
      const msg = e.response?.data?.error || e.message || 'Unknown error';
      setErr(msg);
    }
    setLoading(false);
  }, [propertyId, propertyName, startDate, endDate, saveToHistory, getHistory]);

  const loadHistoryItem = (item) => {
    setInsights(item.insights);
    setShowHistory(false);
  };

  const removeHistoryItem = (id) => {
    deleteHistoryItem(id);
    setHistory(getHistory());
  };

  const scoreColor = s => s>=75?'var(--success)':s>=50?'var(--warning)':'var(--danger)';
  const healthBg   = h => h==='Good'?'rgba(52,211,153,0.08)':h==='Fair'?'rgba(251,191,36,0.08)':'rgba(248,113,113,0.08)';

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

      {/* ── Hero ─────────────────────────────── */}
      <div style={styles.hero}>
        <div style={styles.heroBg} />
        <div style={styles.heroContent}>
          <div style={styles.heroIconRow}>
            <span style={styles.heroBigIcon}>✧</span>
            <div style={styles.heroTagRow}>
              <span style={styles.tag}>AI-Powered</span>
              <span style={styles.tag}>Real-time</span>
              <span style={styles.tag}>Auto-Insights</span>
            </div>
          </div>
          <div style={styles.heroTitle}>AI Insights</div>
          <div style={styles.heroSub}>Let AI analyze your analytics & reveal hidden growth opportunities. Powered by LLaMA 3.3 70B.</div>
          <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
            <button style={styles.genBtn} onClick={generate} disabled={loading}>
              {loading ? `${progress || 'Generating...'}` : '✨ Generate Insights'}
            </button>
            <button style={{...styles.genBtn, background:'var(--surface2)', color:'var(--text)', border:'1px solid var(--border)'}} onClick={() => setShowHistory(!showHistory)}>
              📋 History ({history.length})
            </button>
          </div>
          {err && <div style={styles.errMsg}>{err}</div>}
        </div>
      </div>

      {/* ── History Panel ────────────────────── */}
      {showHistory && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>📋 Saved Insights (Auto-delete after 24h)</div>
          {history.length === 0 ? (
            <div style={{ color:'var(--text3)', fontSize:'clamp(11px, 2vw, 13px)', padding:20, textAlign:'center' }}>
              No saved insights yet. Generate your first one!
            </div>
          ) : (
            <div style={styles.historyList}>
              {history.map((item) => (
                <div key={item.id} style={styles.historyItem}>
                  <div style={{flex:1}}>
                    <div style={{ fontSize:'clamp(12px, 2vw, 13px)', fontWeight:600, color:'var(--text)' }}>{item.propertyName}</div>
                    <div style={{ fontSize:'clamp(10px, 1.8vw, 11px)', color:'var(--text3)', marginTop:4 }}>
                      {item.dateRange.startDate} → {item.dateRange.endDate}
                      <span style={{marginLeft:12}}>
                        {new Date(item.timestamp).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div style={{display:'flex', gap:8}}>
                    <button style={styles.historyLoadBtn} onClick={() => loadHistoryItem(item)}>Load</button>
                    <button style={styles.historyDeleteBtn} onClick={() => removeHistoryItem(item.id)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Insights Content ─────────────────── */}
      {insights && (
        <>
          {/* ── Health Card ──────────────────────── */}
          <div style={{ ...styles.healthCard, background: healthBg(insights.overallHealth) }}>
            <div style={styles.healthLeft}>
              <div style={styles.healthBadge}>
                <div style={{ ...styles.healthDot, background: insights.overallHealth==='Good'?'var(--success)':insights.overallHealth==='Fair'?'var(--warning)':'var(--danger)' }} />
                OVERALL HEALTH
              </div>
              <div style={styles.healthStatus}>{insights.overallHealth || 'Fair'}</div>
              <div style={styles.healthSummary}>{insights.summary}</div>
              <div style={styles.healthTags}>
                {(insights.topTopics||[]).slice(0,5).map((t,i)=>(
                  <span key={i} style={styles.healthTag}>{t}</span>
                ))}
              </div>
            </div>
            <div style={{minWidth:'clamp(100px, 20vw, 130px)', display:'flex', justifyContent:'center'}}>
              <ScoreCircle score={insights.healthScore || 65} />
            </div>
          </div>

          {/* ── Quick Revenue Alert ──────────────── */}
          {insights.revenueSummary && (
            <div style={styles.revCard}>
              <div style={styles.revCardIcon}>💰</div>
              <div>
                <div style={{ fontSize:'clamp(12px, 2vw, 13px)', fontWeight:700, color:'var(--text)', marginBottom:4 }}>Revenue Insight</div>
                <div style={{ fontSize:'clamp(11px, 1.8vw, 12px)', color:'var(--text2)', lineHeight:1.65 }}>
                  {insights.revenueSummary}
                </div>
              </div>
            </div>
          )}

          {/* ── Dev Actions ──────────────────────── */}
          {insights.devActions && insights.devActions.length > 0 && (
            <>
              <SectionTitle icon="🛠" text="Dev Actions" />
              <div style={styles.devActList}>
                {insights.devActions.map((act,i)=>(
                  <div key={i} style={styles.devAct}>
                    <div style={styles.devActHeader}>
                      <span style={styles.devActArea}>{act.area}</span>
                      {act.priority && <PriorityBadge p={act.priority} />}
                      {act.impact && <span style={styles.devActImpact}>{act.impact}</span>}
                    </div>
                    <div style={styles.devActTitle}>{act.action}</div>
                    {act.implementation && (
                      <div style={styles.devActImpl}>
                        <div style={styles.devActImplLabel}>Implementation</div>
                        <div style={styles.devActImplText}>{act.implementation}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── Retention Insights ─────────────── */}
          {insights.retentionInsights && insights.retentionInsights.length > 0 && (
            <>
              <SectionTitle icon="🔄" text="Retention Insights" />
              <div style={styles.retentionGrid}>
                {insights.retentionInsights.map((r,i)=>(
                  <div key={i} style={styles.retCard}>
                    <div style={{ fontSize:'clamp(12px, 2vw, 13px)', fontWeight:600, color:'var(--text)', marginBottom:8 }}>
                      {r.title}
                    </div>
                    <div style={{ fontSize:'clamp(11px, 1.8vw, 12px)', color:'var(--text2)', lineHeight:1.65 }}>
                      {r.detail}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── Tips ───────────────────────────── */}
          {insights.tips && insights.tips.length > 0 && (
            <>
              <SectionTitle icon="💡" text="Quick Tips" />
              <div style={styles.tipsGrid}>
                {insights.tips.map((t,i)=>(
                  <div key={i} style={{...styles.tipCard, borderColor:'rgba(56,189,248,0.2)', border:'1px solid'}}>
                    <div style={styles.tipTop}>
                      <span style={styles.tipNum}>Tip {i+1}</span>
                    </div>
                    <div style={{ fontSize:'clamp(11px, 1.8vw, 12px)', color:'var(--text2)', lineHeight:1.65 }}>
                      {t}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── Key Findings ─────────────────────── */}
          <SectionTitle icon="◈" text="Key Findings" />
          <div style={styles.findingsGrid}>
            {(insights.keyFindings||[]).map((f,i)=>{
              const c = f.type==='positive'?'var(--success)':f.type==='negative'?'var(--danger)':'var(--warning)';
              return (
                <div key={i} style={{ ...styles.findCard, borderColor:c+'33' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                    <div style={{ width:8, height:8, borderRadius:'50%', background:c, flexShrink:0 }} />
                    <div style={{ fontSize:'clamp(12px, 2vw, 13px)', fontWeight:600, color:'var(--text)' }}>{f.title}</div>
                  </div>
                  <div style={{ fontSize:'clamp(11px, 1.8vw, 12px)', color:'var(--text2)', lineHeight:1.65 }}>{f.detail}</div>
                </div>
              );
            })}
          </div>

          {/* ── Metric deep-dives ────────────────── */}
          <SectionTitle icon="◎" text="Metric Deep-Dive" />
          <div style={styles.metricsGrid}>
            {[
              { label:'Active Users',    v:insights.activeUserInsight,  icon:'👥', c:'var(--accent)'  },
              { label:'User Retention',  v:insights.retentionInsight,   icon:'🔄', c:'var(--accent2)' },
              { label:'Engagement',      v:insights.performanceInsight, icon:'⚡', c:'var(--accent3)' },
              { label:'Ad Revenue',      v:insights.adInsight,          icon:'📢', c:'#f472b6'        },
              { label:'Purchases / IAP', v:insights.purchaseInsight,    icon:'🛒', c:'#fb923c'        },
              { label:'User Growth',     v:insights.growthInsight,      icon:'📈', c:'#fbbf24'        },
            ].filter(m=>m.v).map((m,i)=>(
              <div key={i} style={{ ...styles.metricCard, borderColor:m.c+'33' }}>
                <div style={{ fontSize:'clamp(18px, 3vw, 20px)', marginBottom:8 }}>{m.icon}</div>
                <div style={{ fontSize:'clamp(9px, 1.6vw, 10px)', fontWeight:700, color:m.c, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.09em' }}>{m.label}</div>
                <div style={{ fontSize:'clamp(11px, 1.8vw, 12px)', color:'var(--text2)', lineHeight:1.65 }}>{m.v}</div>
              </div>
            ))}
          </div>

          {/* ── Improvements ─────────────────────── */}
          <SectionTitle icon="✦" text="Prioritised Improvements" />
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {(insights.improvements||[]).map((imp,i)=>(
              <div key={i} style={styles.improveCard}>
                <div style={{ display:'flex', flexDirection:'column', gap:6, minWidth:100, flexShrink:0 }}>
                  <PriorityBadge p={imp.priority} />
                  <span style={{ fontSize:'clamp(10px, 1.8vw, 11px)', color:'var(--text3)', fontWeight:600 }}>{imp.area}</span>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:'clamp(12px, 2vw, 13px)', color:'var(--text)', fontWeight:500, marginBottom:4 }}>{imp.action}</div>
                  <div style={{ fontSize:'clamp(10px, 1.8vw, 11px)', color:'var(--accent3)' }}>Expected: {imp.expectedImpact}</div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Quick Wins ───────────────────────── */}
          <SectionTitle icon="⚡" text="Quick Wins" sub="Do these this week" />
          <div style={styles.quickWinsGrid}>
            {(insights.quickWins||[]).map((qw,i)=>(
              <div key={i} style={styles.quickWin}>
                <div style={styles.quickNum}>{i+1}</div>
                <div style={{ fontSize:'clamp(11px, 1.8vw, 12px)', color:'var(--text2)', lineHeight:1.6 }}>{qw}</div>
              </div>
            ))}
          </div>

          {/* ── Ad Revenue Tips ────────────────────── */}
          {insights.adRevenueTips && insights.adRevenueTips.length > 0 && (
            <>
              <SectionTitle icon="📢" text="Ad Revenue Optimization" sub="Increase ad revenue" />
              <div style={styles.tipsGrid}>
                {insights.adRevenueTips.map((t,i)=>(
                  <div key={i} style={{...styles.tipCard, borderColor:'rgba(244,114,182,0.2)', border:'1px solid'}}>
                    <div style={styles.tipTop}>
                      <span style={{...styles.tipNum, border:'1px solid rgba(244,114,182,0.3)', color:'#f472b6', background:'rgba(244,114,182,0.1)'}}>Ad Tip {i+1}</span>
                    </div>
                    <div style={{ fontSize:'clamp(11px, 1.8vw, 13px)', color:'var(--text2)', lineHeight:1.65 }}>{t}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── IAP / Purchase Tips ─────────────────── */}
          {insights.purchaseTips && insights.purchaseTips.length > 0 && (
            <>
              <SectionTitle icon="🛒" text="IAP & Purchase Optimization" sub="Increase in-app purchases" />
              <div style={styles.tipsGrid}>
                {insights.purchaseTips.map((t,i)=>(
                  <div key={i} style={{...styles.tipCard, borderColor:'rgba(251,146,60,0.2)', border:'1px solid'}}>
                    <div style={styles.tipTop}>
                      <span style={{...styles.tipNum, border:'1px solid rgba(251,146,60,0.3)', color:'#fb923c', background:'rgba(251,146,60,0.1)'}}>IAP Tip {i+1}</span>
                    </div>
                    <div style={{ fontSize:'clamp(11px, 1.8vw, 13px)', color:'var(--text2)', lineHeight:1.65 }}>{t}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── More Topics ────────────────────────── */}
          {insights.topTopics && insights.topTopics.length > 0 && (
            <>
              <SectionTitle icon="🏷" text="Report Topics" sub="Areas covered in this analysis" />
              <div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>
                {insights.topTopics.map((t,i)=>(
                  <span key={i} style={{
                    padding:'8px 16px', borderRadius:24,
                    background:'var(--surface)', border:'1px solid var(--border)',
                    fontSize:'clamp(11px, 1.8vw, 13px)', color:'var(--text2)',
                    fontWeight:500, display:'flex', alignItems:'center', gap:6,
                  }}>
                    <span style={{ width:7, height:7, borderRadius:'50%', background:'var(--accent2)', display:'inline-block' }} />
                    {t}
                  </span>
                ))}
              </div>
            </>
          )}

        </>
      )}
    </div>
  );
}

const styles = {
  hero: { position:'relative', overflow:'hidden', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:16 },
  heroBg: { position:'absolute', top:-80, right:-80, width:300, height:300, borderRadius:'50%', background:'radial-gradient(circle,rgba(129,140,248,0.12) 0%,transparent 70%)', pointerEvents:'none' },
  heroContent: { position:'relative', zIndex:1, padding:'36px 40px', textAlign:'center' },
  heroIconRow: { display:'flex', flexDirection:'column', alignItems:'center', gap:12, marginBottom:16 },
  heroBigIcon: { fontSize:'clamp(36px, 8vw, 48px)', color:'var(--accent2)' },
  heroTagRow:  { display:'flex', gap:8, flexWrap:'wrap', justifyContent:'center' },
  tag:         { padding:'4px 12px', background:'rgba(56,189,248,0.1)', border:'1px solid rgba(56,189,248,0.2)', borderRadius:20, fontSize:'clamp(9px, 1.6vw, 10px)', fontWeight:700, color:'var(--accent)', letterSpacing:'0.05em' },
  heroTitle:   { fontSize:'clamp(20px, 5vw, 24px)', fontWeight:800, color:'var(--text)', letterSpacing:'-0.5px', marginBottom:10 },
  heroSub:     { fontSize:'clamp(11px, 2vw, 13px)', color:'var(--text3)', lineHeight:1.75, marginBottom:24, maxWidth:560, margin:'0 auto 24px' },
  genBtn:      { padding:'14px 36px', background:'linear-gradient(135deg,#818cf8,#38bdf8)', border:'none', borderRadius:12, color:'#fff', fontWeight:700, fontSize:'clamp(12px, 2vw, 15px)', cursor:'pointer', boxShadow:'0 6px 24px rgba(129,140,248,0.3)', minWidth:200 },
  errMsg:      { marginTop:12, color:'var(--danger)', fontSize:'clamp(10px, 1.8vw, 12px)' },

  section:      { background:'var(--surface)', border:'1px solid var(--border)', borderRadius:14, padding:20 },
  sectionTitle: { fontSize:'clamp(12px, 2.5vw, 13px)', fontWeight:700, color:'var(--text)', marginBottom:14 },

  historyList: { display:'flex', flexDirection:'column', gap:10 },
  historyItem: { background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' },
  historyLoadBtn: { padding:'6px 16px', background:'var(--accent)', border:'none', borderRadius:6, color:'#fff', fontSize:'clamp(10px, 1.8vw, 12px)', fontWeight:600, cursor:'pointer' },
  historyDeleteBtn: { padding:'6px 16px', background:'var(--danger)', border:'none', borderRadius:6, color:'#fff', fontSize:'clamp(10px, 1.8vw, 12px)', fontWeight:600, cursor:'pointer' },

  healthCard:   { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'28px 32px', borderRadius:14, border:'1px solid var(--border)', flexWrap:'wrap', gap:20 },
  healthLeft:   { flex:1, minWidth:240 },
  healthBadge:  { display:'flex', alignItems:'center', gap:6, fontSize:'clamp(9px, 1.6vw, 10px)', color:'var(--text3)', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:8 },
  healthDot:    { width:8, height:8, borderRadius:'50%', flexShrink:0 },
  healthStatus: { fontSize:'clamp(24px, 6vw, 34px)', fontWeight:800, letterSpacing:'-1px', marginBottom:10 },
  healthSummary:{ fontSize:'clamp(11px, 2vw, 13px)', color:'var(--text2)', lineHeight:1.7, maxWidth:480, marginBottom:12 },
  healthTags:   { display:'flex', flexWrap:'wrap', gap:6 },
  healthTag:    { padding:'3px 10px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:20, fontSize:'clamp(9px, 1.6vw, 10px)', color:'var(--text3)' },

  revCard:      { background:'linear-gradient(135deg,rgba(244,114,182,0.06),rgba(56,189,248,0.06))', border:'1px solid rgba(244,114,182,0.2)', borderRadius:12, padding:'18px 20px', display:'flex', gap:14, alignItems:'flex-start' },
  revCardIcon:  { fontSize:'clamp(24px, 5vw, 28px)', flexShrink:0, marginTop:2 },

  tipsGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:14 },
  tipCard:  { background:'var(--surface)', border:'1px solid', borderRadius:12, padding:'16px 18px' },
  tipTop:   { marginBottom:10 },
  tipNum:   { padding:'3px 10px', borderRadius:20, fontSize:'clamp(8px, 1.5vw, 9px)', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', border:'1px solid rgba(56,189,248,0.3)', color:'var(--accent)', background:'rgba(56,189,248,0.1)' },

  devActList: { display:'flex', flexDirection:'column', gap:12 },
  devAct:     { background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:'16px 20px' },
  devActHeader:{ display:'flex', alignItems:'center', gap:10, marginBottom:10, flexWrap:'wrap' },
  devActArea: { fontSize:'clamp(11px, 1.8vw, 12px)', color:'var(--text3)', fontWeight:600 },
  devActImpact:{ fontSize:'clamp(10px, 1.6vw, 11px)', color:'var(--accent3)', marginLeft:'auto' },
  devActTitle: { fontSize:'clamp(12px, 2vw, 14px)', fontWeight:600, color:'var(--text)', marginBottom:8, lineHeight:1.5 },
  devActImpl:  { background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, padding:'10px 14px', marginTop:8 },
  devActImplLabel:{ fontSize:'clamp(8px, 1.5vw, 9px)', fontWeight:700, color:'var(--text3)', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:6 },
  devActImplText:{ fontSize:'clamp(10px, 1.8vw, 12px)', color:'var(--text2)', lineHeight:1.7 },

  retentionGrid:{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:14 },
  retCard:      { background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:'18px 16px' },

  findingsGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:14 },
  findCard:     { background:'var(--surface)', border:'1px solid', borderRadius:12, padding:'16px 18px' },

  metricsGrid:  { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:14 },
  metricCard:   { background:'var(--surface)', border:'1px solid', borderRadius:12, padding:'18px 16px' },

  improveCard:  { background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:'14px 18px', display:'flex', alignItems:'flex-start', gap:18 },

  quickWinsGrid:{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:12 },
  quickWin:     { background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:'14px 18px', display:'flex', alignItems:'flex-start', gap:12 },
  quickNum:     { width:26, height:26, borderRadius:8, background:'rgba(56,189,248,0.15)', color:'var(--accent)', fontWeight:800, fontSize:'clamp(10px, 1.8vw, 12px)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontFamily:'Space Mono,monospace' },
};
